import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, debounce, Grid, MenuItem, Typography } from '@material-ui/core';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { GridSize } from '@material-ui/core/Grid/Grid';
import { List as ImmutableList } from 'immutable';
import { Select, ListboxComponent, AvatarWithInitials, TextField } from 'common/components';
import inputLabels from 'common/messages/inputLabels';
import { CameFromType } from 'common/constants';
import { ICameFromIndicatorDictionaryItemImt } from 'common/interfaces/dictionary';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import { INamedEntity } from 'common/interfaces/common';
import { getAccessByPropPath } from 'common/utils/errorObject';
import { getInitials } from 'helpers/common';
import { formatPersonalCode } from 'common/utils';
import Autocomplete from 'common/components/Autocomplete/Autocomplete';
import { SearchReferralMembersReturn } from 'common/hooks/useSearchReferralMembers';

type TLayout = { xs?: GridSize; sm?: GridSize };

interface ILayoutConfig {
  cameFromField: TLayout;
  referralListField: TLayout;
  dropdownSelectField: TLayout;
  singleInputField: TLayout;
}

interface ICameFromFieldsProps {
  cameFromIndicators?: ImmutableList<ICameFromIndicatorDictionaryItemImt>;
  getReferralsByValue: SearchReferralMembersReturn;
  isMultipleSelect?: boolean;
  layoutConfig?: ILayoutConfig;
  cameFromIndicator?: any;
  referralMember?: any;
  fieldName?: string;
  personId?: number;
}

const useStyles = makeStyles(() => ({
  optionAvatar: {
    width: '36px',
    height: '36px',
  },
  optionName: {
    fontSize: '0.9rem',
    fontWeight: 500,
  },
}));

const CameFromFields: FC<ICameFromFieldsProps> = ({
  cameFromIndicators,
  getReferralsByValue,
  layoutConfig = {},
  referralMember,
  cameFromIndicator,
  fieldName,
  isMultipleSelect,
  personId,
}) => {
  const {
    cameFromField,
    referralListField,
    dropdownSelectField,
    singleInputField,
  } = layoutConfig as ILayoutConfig;

  const [currentCameFrom, setCurrentCameFrom] = useState<ICameFromIndicatorDictionaryItemImt>();
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [referrals, setReferrals] = useState<INamedEntity[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const { control, formState, setValue } = useFormContext();
  const { errors } = formState;
  const renderIntlMessage = useRenderIntlMessage();
  const classes = useStyles();

  const { cameFromTypeFieldName, optionalFieldName } = useMemo(
    () => ({
      cameFromTypeFieldName: fieldName ? `${fieldName}.cameFromType` : 'cameFromType',
      optionalFieldName: fieldName ? `${fieldName}.optional` : 'optional',
    }),
    [fieldName],
  );

  useEffect(() => {
    if (currentCameFrom) {
      setValue(optionalFieldName, currentCameFrom.get('optional'));
      setValue(cameFromTypeFieldName, currentCameFrom.get('type'));
    }
  }, [cameFromTypeFieldName, currentCameFrom, optionalFieldName, setValue]);

  const cameFromType = useWatch({
    control,
    name: cameFromTypeFieldName,
  });

  const personsList = referrals || [];

  const loadReferrals = useCallback(
    async (search: string, reset: boolean = false) => {
      if (reset) {
        setPage(0);
        setHasMore(true);
      }
      const currentPage = reset ? 0 : page;
      await getReferralsByValue({
        search: search ?? '',
        page: currentPage,
        personId: personId || 0,
      })
        .unwrap()
        .then(response => {
          if (reset) {
            setReferrals(response.data || []);
          } else {
            setReferrals(prev => [...prev, ...response.data]);
          }
          setHasMore(response.data.length === 30);
          setPage(prev => prev + 1);
        });
    },
    [getReferralsByValue, page, personId],
  );

  const onChangeSearch = useMemo(() => {
    return debounce((value: string) => {
      setSearchTerm(value);
      loadReferrals(value, true);
    }, 500);
  }, [loadReferrals]);

  const onScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const list = event.currentTarget;
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 5 && !loadingMore && hasMore) {
      setLoadingMore(true);
      loadReferrals(searchTerm).finally(() => setLoadingMore(false));
    }
  };

  const renderUserOption = ({ title, imageUrl, id }: INamedEntity) => {
    const [firstName, lastName] = title.split(' ');

    const initials = getInitials(firstName, lastName);

    return (
      <Box display="flex">
        <AvatarWithInitials
          imageUrl={imageUrl}
          width="36px"
          height="36px"
          fontSize="1rem"
          initials={initials}
        />

        <Box display="flex" alignItems="center">
          <Typography className={classes.optionName}>{`${formatPersonalCode(
            Number(id),
          )} - ${title}`}</Typography>
        </Box>
      </Box>
    );
  };

  const existReferralMember = referralMember
    ? [
        {
          id: referralMember.get('id'),
          title: `${referralMember.get('firstName')} ${referralMember.get('lastName')}`,
          imageUrl: referralMember.get('imageUrl'),
        },
      ]
    : [];

  const options = personsList.length ? personsList : existReferralMember;

  const ListboxProps = {
    itemSize: 45,
    limitCount: 8,
    onScroll,
  };

  return (
    <>
      <Grid item xs={12} {...cameFromField}>
        <Controller
          name={fieldName ? `${fieldName}.cameFromIndicatorId` : 'cameFromIndicatorId'}
          control={control}
          render={({ field }) => (
            <Select
              label={<FormattedMessage {...inputLabels.cameFrom} />}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              fullWidth
              variant="outlined"
              error={
                !!(fieldName
                  ? getAccessByPropPath(errors, `${fieldName}.cameFromIndicatorId`)
                  : errors.cameFromIndicatorId)
              }
              helperText={renderIntlMessage(
                fieldName
                  ? renderIntlMessage(get(errors, `${fieldName}.cameFromIndicatorId.message`))
                  : renderIntlMessage(errors.cameFromIndicatorId?.message),
              )}
            >
              {cameFromIndicators?.map(item => (
                <MenuItem
                  onClick={() => setCurrentCameFrom(item)}
                  key={item.get('id')}
                  value={item.get('id')}
                >
                  {item.get('title')}
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </Grid>

      {cameFromType === CameFromType.ReferralList && (
        <Grid item xs={12} {...referralListField}>
          <Controller
            control={control}
            name={fieldName ? `${fieldName}.referralMember` : 'referralMember'}
            render={({ field }) => (
              <Autocomplete
                open={isOpenDropdown}
                onOpen={() => {
                  setIsOpenDropdown(true);
                  if (referrals.length === 0) {
                    loadReferrals('', true);
                  }
                }}
                onClose={() => setIsOpenDropdown(false)}
                onChange={(e, val) => {
                  field.onChange(val);
                }}
                inputValue={field.value?.title}
                multiple={isMultipleSelect}
                disableCloseOnSelect={isMultipleSelect}
                value={field.value}
                getOptionLabel={option => {
                  const code = formatPersonalCode(Number(option.id));

                  return option.title
                    ? `${code} - ${option.title}`
                    : `${code} - ${option.firstName} ${option.lastName}`;
                }}
                renderOption={renderUserOption}
                onInputChange={(e, val) => {
                  onChangeSearch(val || '');
                }}
                onFocus={(e: React.BaseSyntheticEvent) => {
                  onChangeSearch(e.target.value || '');
                }}
                onBlur={() => {
                  field.onBlur();
                  setReferrals([]);
                  setHasMore(true);
                  setPage(0);
                }}
                ListboxComponent={ListboxComponent}
                ListboxProps={ListboxProps}
                options={options}
                loading={loadingMore}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={
                      <FormattedMessage
                        {...(currentCameFrom?.get('optional')
                          ? inputLabels.referralOptional
                          : inputLabels.referral)}
                      />
                    }
                    variant="outlined"
                    fullWidth
                    error={
                      !!(fieldName
                        ? getAccessByPropPath(errors, `${fieldName}.referralMember`)
                        : errors.cameFromIndicatorId)
                    }
                    helperText={renderIntlMessage(
                      fieldName
                        ? renderIntlMessage(get(errors, `${fieldName}.referralMember.message`))
                        : renderIntlMessage(errors.cameFromIndicatorId?.message),
                    )}
                    InputProps={{
                      ...params.InputProps,
                      autoComplete: 'off',
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
              />
            )}
          />
        </Grid>
      )}

      {cameFromType === CameFromType.DropdownSelect && (
        <Grid item xs={12} {...dropdownSelectField}>
          <Controller
            control={control}
            name={fieldName ? `${fieldName}.cameFromValue` : 'cameFromValue'}
            render={({ field }) => (
              <Select
                fullWidth
                variant="outlined"
                label={
                  currentCameFrom?.get('inputPlaceholder') ||
                  cameFromIndicator?.get('inputPlaceholder')
                }
                multiple={isMultipleSelect}
                name={field.name}
                value={!field.value && isMultipleSelect ? [] : field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={
                  !!(fieldName
                    ? getAccessByPropPath(errors, `${fieldName}.cameFromValue`)
                    : errors.cameFromValue)
                }
                helperText={renderIntlMessage(
                  fieldName
                    ? renderIntlMessage(get(errors, `${fieldName}.cameFromValue.message`))
                    : renderIntlMessage(errors.cameFromValue?.message),
                )}
              >
                {(currentCameFrom?.get('options') || cameFromIndicator?.get('options'))?.map(
                  (option: string, index: number) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ),
                )}
              </Select>
            )}
          />
        </Grid>
      )}

      {cameFromType === CameFromType.SingleInput && (
        <Grid item xs={12} {...singleInputField}>
          <Controller
            control={control}
            name={fieldName ? `${fieldName}.cameFromValue` : 'cameFromValue'}
            render={({ field }) => (
              <TextField
                fullWidth
                label={
                  currentCameFrom?.get('inputPlaceholder') ||
                  cameFromIndicator?.get('inputPlaceholder')
                }
                variant="outlined"
                inputProps={{ maxLength: 1000 }}
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={field.ref}
                error={
                  !!(fieldName
                    ? getAccessByPropPath(errors, `${fieldName}.cameFromValue`)
                    : errors.cameFromValue)
                }
                helperText={renderIntlMessage(
                  fieldName
                    ? renderIntlMessage(get(errors, `${fieldName}.cameFromValue.message`))
                    : renderIntlMessage(errors.cameFromValue?.message),
                )}
              />
            )}
          />
        </Grid>
      )}

      {/* TODO - PRM-3575 need types */}
      <Controller render={() => null as any} control={control} name={optionalFieldName} />
      <Controller render={() => null as any} control={control} name={cameFromTypeFieldName} />
    </>
  );
};

export default CameFromFields;
